/**
 * OrderDetailExtra — the body of the order's trace rail tab (admin only).
 *
 * It holds ONLY the troubleshooting trace: the Re-fetch command is the header
 * toolbar (`OrderActionsToolbar`).
 *
 * The trace is its own rail tab since #1921, so this renders bare panel
 * content — no Card, no header, no collapse toggle. The rail trigger is the
 * title AND the disclosure now, so repeating either here would state it twice.
 *
 * Laziness survived the move without a flag of its own. The card gated its
 * queries on `open`; an INACTIVE rail panel is not mounted at all
 * (`TabsContent` → `@base-ui/react` `Tabs.Panel`, whose `keepMounted` defaults
 * to `false`), so mounted IS activated and `enabled` needs no second signal.
 *
 * The `isAdmin` check stays even though the tab gate already hides the whole
 * panel from a non-admin: it mirrors the backend's `requireAdmin()`, and a
 * mirror a wiring mistake can switch off is not a mirror. `schema.tsx` feeds
 * the same fact to the rail through `host.detail.tabGates`.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useBetterAuth } from '@hooks/useAuth';
import { queryKeys } from '@lib/queryKeys';
import { orderAPI, type Order } from '@/services';
import { TraceTimeline } from './TraceTimeline';

interface OrderDetailExtraProps {
  order: Order;
}

export function OrderDetailExtra({ order }: OrderDetailExtraProps) {
  const { t } = useTranslation();
  const { isAdmin } = useBetterAuth();
  // Trace panel mirrors the backend `requireAdmin()` gate.
  const canViewTrace = isAdmin;

  // The trace timeline still consumes the order's jobs (its fulfillment-job
  // events). Shared query key with `OrderStructureTree`, so an operator who
  // has already opened the delivery-jobs tab resolves this from cache.
  const jobsQuery = useQuery({
    queryKey: queryKeys.orders.jobs(order.shopify_id),
    queryFn: () => orderAPI.getOrderTasks(order.shopify_id),
    staleTime: 60_000,
    enabled: canViewTrace,
  });
  const jobs = jobsQuery.data?.items ?? [];

  if (!canViewTrace) return null;

  return (
    <div>
      <p className="text-xs text-muted-foreground">{t('order.trace.sectionDescription')}</p>
      <div id={`order-trace-panel-${order.shopify_id}`} className="mt-3">
        {/* No `enabled` prop: past the early return `canViewTrace` is provably
            true, so passing it would be a gate that reads live and is not.
            NOT rendering the timeline at all is the gate here. */}
        <TraceTimeline shopifyOrderId={order.shopify_id} jobs={jobs} />
      </div>
    </div>
  );
}
